import React, { useEffect, useMemo, useState } from "react";
import SaturdayResearch from "./SaturdayResearch";
import { api } from "../../api";
import {
  LEDGER_COLUMNS,
  LedgerTable,
  Pager,
  SortHeader,
  StatusBadge,
  downloadCsv,
  humanize,
  useDebounced,
  useSort,
} from "./ResearchParts";
import "./football-intelligence.css";
import "./fi-extra.css";

const PAGE = 50;
const f4 = (v) => (typeof v === "number" ? v.toFixed(4) : "—");
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const shortVersion = (v) => String(v || "Research variant").split(":")[0];

const TABS = [
  ["fixtureDay", "Fixture-day coverage"],
  ["models", "Models & coverage"],
  ["ledger", "Hypothetical ledger"],
];

/**
 * Admin research lab. Retrospective evaluation and prospective shadow
 * forecasts are separate populations and are never merged or ranked here.
 */
export default function ResearchOverview() {
  const [tab, setTab] = useState("fixtureDay");
  return (
    <section className="fi-shell">
      <div className="fi-hero">
        <div>
          <div className="fi-eyebrow">MODEL LABORATORY · NO PRODUCTION PROMOTION</div>
          <h2>Research, with a record.</h2>
          <p>Retrospective evaluation and prospective shadows are separate populations.</p>
        </div>
      </div>
      <nav className="fi-tabs" aria-label="Research sections">
        {TABS.map(([key, label]) => (
          <button key={key} aria-pressed={tab === key} onClick={() => setTab(key)}>{label}</button>
        ))}
      </nav>
      {tab === "fixtureDay" && <div style={{ padding: "0 0 8px" }}><SaturdayResearch /></div>}
      {tab === "models" && <ModelsAndCoverage />}
      {tab === "ledger" && <LedgerTab />}
    </section>
  );
}

function useResearch(params) {
  const [state, setState] = useState({ data: null, error: "" });
  const key = JSON.stringify(params);
  useEffect(() => {
    let alive = true;
    api
      .get("/api/football/intelligence/research", { params: JSON.parse(key) })
      .then((response) => alive && setState({ data: response.data, error: "" }))
      .catch((e) => alive && setState({ data: null, error: e.response?.data?.detail || "Research data unavailable" }));
    return () => {
      alive = false;
    };
  }, [key]);
  return state;
}

const MODEL_SORT = {
  league: (r) => r._league,
  variant: (r) => r.model,
  market: (r) => r.market,
  n: (r) => r.n,
  logloss: (r) => r.log_loss,
  brier: (r) => r.brier,
};

function ModelsAndCoverage() {
  const { data, error } = useResearch({ mode: "overview" });
  const [showEmpty, setShowEmpty] = useState(false);

  const coverageRows = useMemo(() => data?.coverage?.rows || [], [data]);
  const leagueNames = useMemo(() => new Map(coverageRows.filter((r) => r.pitchapi_league_id).map((r) => [r.pitchapi_league_id, r.competition])), [coverageRows]);
  const models = useMemo(() => (data?.models || []).map((m) => ({ ...m, _league: leagueNames.get(m.league_id) || m.league_id })), [data, leagueNames]);
  const { sorted, sort, toggle } = useSort(models, "league", MODEL_SORT);
  const shownCoverage = coverageRows.filter((r) => showEmpty || r.cached_fixtures > 0);

  if (error) return <p role="alert" className="fi-card">{error}</p>;
  if (!data) return <div className="fi-skeleton">Loading research evidence…</div>;

  const leagues = new Set(models.map((m) => m.league_id)).size;
  return (
    <>
      <div className="fx-pad">
        <div className="fx-tiles">
          <div className="fx-tile"><span>Challenger evaluations</span><strong>{models.length}</strong><small>{leagues} leagues</small></div>
          <div className="fx-tile"><span>Competitions tracked</span><strong>{data.coverage?.competitions ?? "—"}</strong><small>actual / configured</small></div>
          <div className="fx-tile"><span>Mapped to PitchAPI</span><strong>{data.coverage?.mapped_competitions ?? "—"}</strong><small>identity only</small></div>
          <div className="fx-tile"><span>Production promotion</span><strong>{data.production_promoted ? "Yes" : "No"}</strong><small>research only</small></div>
        </div>
        <div className="fx-callout">
          <strong>Production Model A:</strong> {data.production_comparison ? humanize(data.production_comparison) : "No matched evidence supplied"}. Model C remains isolated and is not exposed here. No ROI or CLV is inferred from unsettled forecasts.
        </div>
      </div>

      <div className="fx-pad">
        <h3 style={{ margin: "0 0 4px" }}>Retrospective challenger evaluation</h3>
        <p className="fi-note">Held-out test matches only. Variants differ by small margins on small samples, so a lower number is not evidence of an improvement. All rows are revision-unsafe retrospective results.</p>
        <div className="fx-scroll">
          <table style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <SortHeader label="League" k="league" sort={sort} toggle={toggle} />
                <SortHeader label="Variant" k="variant" sort={sort} toggle={toggle} />
                <SortHeader label="Market" k="market" sort={sort} toggle={toggle} />
                <SortHeader label="Test N" k="n" sort={sort} toggle={toggle} numeric />
                <SortHeader label="Log loss" k="logloss" sort={sort} toggle={toggle} numeric />
                <SortHeader label="Brier" k="brier" sort={sort} toggle={toggle} numeric />
                <th>Training → calibration cutoff</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m, i) => (
                <tr key={`${m.league_id}-${m.model}-${m.market}-${i}`}>
                  <td>{m._league}</td>
                  <td>{m.model}<small>{shortVersion(m.model_version)}</small></td>
                  <td>{m.market}</td>
                  <td className="fx-num">{m.n}</td>
                  <td className="fx-num">{f4(m.log_loss)}</td>
                  <td className="fx-num">{f4(m.brier)}</td>
                  <td>{day(m.training_cutoff)} → {day(m.calibration_cutoff)}</td>
                  <td><StatusBadge value={m.mode} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="fx-pad">
        <h3 style={{ margin: "8px 0 4px" }}>Provider coverage</h3>
        <p className="fi-note">{data.coverage?.competitions ?? "—"} actual/configured competitions · {data.coverage?.mapped_competitions ?? "—"} identity-mapped to PitchAPI. Mapping does not prove advanced coverage.</p>
        <label className="fx-check"><input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />Include competitions with no cached data ({coverageRows.length} total)</label>
        <div className="fx-scroll" style={{ marginTop: 10 }}>
          <table style={{ minWidth: 820 }}>
            <thead>
              <tr><th>Competition</th><th>Season</th><th className="fx-num">Listed / cached</th><th className="fx-num">xG / advanced</th><th className="fx-num">Team totals</th><th className="fx-num">Player matches</th><th>Data quality</th><th>Model readiness</th></tr>
            </thead>
            <tbody>
              {shownCoverage.map((r, i) => (
                <tr key={`${r.csb_league_id}-${r.season}-${i}`}>
                  <td>{r.competition}<small>{r.country || ""}</small></td>
                  <td>{r.season}</td>
                  <td className="fx-num">{r.available_historical_fixtures ?? "?"} / {r.cached_fixtures}</td>
                  <td className="fx-num">{r.shots_xg} / {r.advanced_team}</td>
                  <td className="fx-num">{r.team_totals}</td>
                  <td className="fx-num">{r.player_matches}</td>
                  <td><StatusBadge value={r.data_quality} /></td>
                  <td><StatusBadge value={r.model_readiness} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function LedgerTab() {
  const [offset, setOffset] = useState(0);
  const [draft, setDraft] = useState({ model: "", market: "" });
  const filters = useDebounced(draft, 300);
  const { data, error } = useResearch({ mode: "shadow", offset, limit: PAGE, ...filters });

  const change = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setOffset(0);
  };

  if (error) return <p role="alert" className="fi-card">{error}</p>;
  if (!data) return <div className="fi-skeleton">Loading research evidence…</div>;

  return (
    <div className="fx-pad">
      <div className="fx-toolbar">
        <label className="fx-field">Model<input value={draft.model} onChange={(e) => change("model", e.target.value)} placeholder="A / B:xg" /></label>
        <label className="fx-field">Market<input value={draft.market} onChange={(e) => change("market", e.target.value)} placeholder="CORNERS / BTTS" /></label>
        <button type="button" className="fx-button" disabled={!data.rows?.length} onClick={() => downloadCsv(`csb-research-ledger-page${offset / PAGE + 1}.csv`, data.rows, LEDGER_COLUMNS(() => ""))}>Export this page (CSV)</button>
        <span className="fx-coverage"><b>{data.total ?? 0}</b> matching records · includes abstentions</span>
      </div>
      <LedgerTable rows={data.rows} />
      <Pager offset={offset} limit={PAGE} total={data.total ?? 0} hasMore={!!data.has_more} onChange={setOffset} />
      <p className="fi-note">Correlated quotes are not independent bets. Original timestamps are preserved.</p>
    </div>
  );
}
